'use client';

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';

import { cn } from '@/shared/utils/cn';
import { SearchInput } from './SearchInput';
import { SearchSuggestionsDropdown } from './SearchSuggestionsDropdown';
import type { SearchComboboxConfig, SearchComboboxItem } from './types';

const INACTIVE_INDEX = -1;

export type SearchComboboxProps = {
  autoFocus?: boolean;
  className?: string;
  dropdownPlacement?: 'portal' | 'inline';
  id?: string;
  search: SearchComboboxConfig;
};

export type ControlledSearchBarWidgetProps = SearchComboboxProps;

export function SearchCombobox({
  autoFocus,
  className,
  dropdownPlacement = 'portal',
  id,
  search,
}: SearchComboboxProps) {
  const reactId = useId();
  const inputId = id ?? `search-bar-${reactId}`;
  const previewId = `${inputId}-preview`;
  const optionIdPrefix = `${inputId}-option`;
  const containerRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(INACTIVE_INDEX);

  useEffect(() => {
    setActiveIndex(INACTIVE_INDEX);
  }, [search.previewItems]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) return;
      if ((event.target as HTMLElement)?.closest?.('.adipa-search-bar-dropdown')) return;
      setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const closePreview = useCallback(() => {
    setIsOpen(false);
    setActiveIndex(INACTIVE_INDEX);
  }, []);

  const handleQueryChange = useCallback(
    (nextValue: string) => {
      search.onQueryChange(nextValue);
      setIsOpen(nextValue.trim().length > 0);
    },
    [search],
  );

  const handleSubmit: SearchComboboxConfig['onSubmit'] = useCallback(
    (event, nextQuery) => {
      closePreview();
      search.onSubmit(event, nextQuery);
    },
    [closePreview, search],
  );

  const handleSelect = useCallback(
    (item: SearchComboboxItem) => {
      closePreview();
      search.onPreviewSelect(item);
    },
    [closePreview, search],
  );

  const comboboxSearch = useMemo<SearchComboboxConfig>(
    () => ({
      ...search,
      onPreviewSelect: handleSelect,
      onQueryChange: handleQueryChange,
      onSubmit: handleSubmit,
    }),
    [handleQueryChange, handleSelect, handleSubmit, search],
  );

  const previewEnabled = search.previewEnabled ?? true;
  const showPreview = previewEnabled && isOpen && search.query.trim().length > 0;
  const showEmpty = showPreview && !search.isLoading && search.previewItems.length === 0;
  const popupVisible = showPreview && (search.previewItems.length > 0 || showEmpty);

  const handleInputKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape') {
        if (isOpen) {
          event.preventDefault();
          closePreview();
        }
        return;
      }

      if (event.key === 'ArrowDown') {
        if (search.previewItems.length === 0) return;
        event.preventDefault();
        setIsOpen(true);
        setActiveIndex((previous) => (previous + 1) % search.previewItems.length);
        return;
      }

      if (event.key === 'ArrowUp') {
        if (search.previewItems.length === 0) return;
        event.preventDefault();
        setIsOpen(true);
        setActiveIndex((previous) =>
          previous <= 0 ? search.previewItems.length - 1 : previous - 1,
        );
        return;
      }

      if (event.key === 'Enter') {
        if (activeIndex < 0) return;
        const item = search.previewItems[activeIndex];
        if (!item) return;
        event.preventDefault();
        handleSelect(item);
      }
    },
    [activeIndex, closePreview, handleSelect, isOpen, search.previewItems],
  );

  const activeDescendantId =
    popupVisible && activeIndex >= 0 ? `${optionIdPrefix}-${activeIndex}` : undefined;

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      <div ref={anchorRef}>
        <SearchInput
          ariaActiveDescendant={activeDescendantId}
          ariaControls={previewId}
          ariaExpanded={popupVisible}
          autoFocus={autoFocus}
          id={inputId}
          inputRef={inputRef}
          onInputKeyDown={handleInputKeyDown}
          search={comboboxSearch}
        />
      </div>

      {showPreview ? (
        <SearchSuggestionsDropdown
          activeIndex={activeIndex}
          anchorRef={anchorRef}
          emptyStateLabel={search.emptyStateLabel}
          items={search.previewItems}
          optionIdPrefix={optionIdPrefix}
          placement={dropdownPlacement}
          previewId={previewId}
          showEmptyState={showEmpty}
          onSelect={handleSelect}
        />
      ) : null}
    </div>
  );
}

export const ControlledSearchBarWidget = SearchCombobox;

export default SearchCombobox;
